import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Pause, Play, Sparkles } from "lucide-react";

/**
 * <LifestyleVideo/> — a section that shows an original human lifestyle moment
 * alongside a subtle VAULT UI overlay. Behaviour follows the P0 spec:
 *
 *   • Poster still is always rendered first.
 *   • Video (webm/mp4) starts playing only when the section enters the
 *     viewport (IntersectionObserver). Muted, looping, playsInline, no audio.
 *   • Respects `prefers-reduced-motion` → poster only.
 *   • Explicit pause / play control.
 *   • If no video file is configured (VIDEO_ASSET_REQUIRED), the poster is
 *     the final render and a small "VIDEO_ASSET_REQUIRED" badge shows only
 *     when NODE_ENV is not production.
 *   • Mobile/desktop crops selected via media query hook.
 *   • Alt description is provided as accessible text — never as decorative.
 */
export default function LifestyleVideo({ moment, side = "right" }) {
    const [isMobile, setIsMobile] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);
    const [visible, setVisible] = useState(false);
    const [playing, setPlaying] = useState(false);
    const wrapRef = useRef(null);
    const videoRef = useRef(null);

    // media queries
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mm = window.matchMedia("(max-width: 720px)");
        const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
        const sync = () => { setIsMobile(mm.matches); setReducedMotion(rm.matches); };
        sync();
        mm.addEventListener?.("change", sync);
        rm.addEventListener?.("change", sync);
        return () => {
            mm.removeEventListener?.("change", sync);
            rm.removeEventListener?.("change", sync);
        };
    }, []);

    // lazy-load: only mount + play video when section enters viewport
    useEffect(() => {
        if (!wrapRef.current || typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => setVisible(e.isIntersecting));
        }, { rootMargin: "150px 0px" });
        io.observe(wrapRef.current);
        return () => io.disconnect();
    }, []);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (visible && !reducedMotion && playing) v.play().catch(() => {});
        else v.pause();
    }, [visible, reducedMotion, playing]);

    const src = moment
        ? (isMobile ? (moment.video_file_mobile || moment.video_file_desktop)
                    : (moment.video_file_desktop || moment.video_file_mobile))
        : null;
    const poster = moment
        ? (isMobile ? (moment.poster_mobile || moment.poster_desktop)
                    : (moment.poster_desktop || moment.poster_mobile))
        : null;
    const hasVideo = !!src && !reducedMotion;

    // start "playing" attempted state once visible; poster still until then
    useEffect(() => {
        if (visible && hasVideo && !playing) setPlaying(true);
    }, [visible, hasVideo, playing]);

    if (!moment) return null;
    const showBadge = process.env.NODE_ENV !== "production" && !src;

    return (
        <section
            ref={wrapRef}
            className="lm-section"
            data-testid={`lifestyle-${moment.id}`}
            aria-label={moment.headline}
        >
            <style>{LM_CSS}</style>
            <div className={`lm-grid ${side === "left" ? "lm-grid--flip" : ""}`}>
                <div className="lm-copy">
                    <div className="lm-eyebrow">
                        <Sparkles size={11} strokeWidth={2.2} /> {moment.eyebrow?.toUpperCase() || "VAULT"}
                    </div>
                    <h2 className="lm-title">{moment.headline}</h2>
                    <p className="lm-sub">{moment.sub}</p>
                    <div className="lm-ctas">
                        {moment.cta && (
                            <Link to={moment.cta.to} className="lm-cta lm-cta--primary" data-testid={`lifestyle-cta-${moment.id}`}>
                                {moment.cta.label} <ArrowRight size={12} />
                            </Link>
                        )}
                        {moment.cta2 && (
                            <Link to={moment.cta2.to} className="lm-cta lm-cta--ghost">
                                {moment.cta2.label}
                            </Link>
                        )}
                    </div>
                </div>

                <div className="lm-media">
                    {/* Poster — always rendered so incognito visitors + slow connections + reduced-motion + missing-asset all see a beautiful still. */}
                    <img
                        src={poster}
                        alt={moment.alt_description}
                        className="lm-poster"
                        loading="lazy"
                        data-testid={`lifestyle-poster-${moment.id}`}
                    />
                    {/* Video mounts only when a real file has been supplied AND reduced-motion is off AND section is near viewport. */}
                    {hasVideo && visible && (
                        <video
                            ref={videoRef}
                            className="lm-video"
                            muted playsInline loop
                            preload="metadata"
                            poster={poster}
                            aria-hidden="true"
                        >
                            <source src={src} />
                        </video>
                    )}
                    {/* Pause / play control — visible only when a video is actually playing. */}
                    {hasVideo && (
                        <button
                            className="lm-pause"
                            type="button"
                            aria-label={playing ? "Pause video" : "Play video"}
                            onClick={() => setPlaying((p) => !p)}
                            data-testid={`lifestyle-pause-${moment.id}`}
                        >
                            {playing ? <Pause size={12}/> : <Play size={12}/>}
                        </button>
                    )}
                    {/* Subtle floating VAULT UI overlay. */}
                    {moment.overlay && (
                        <div className={`lm-overlay lm-overlay--${moment.overlay.kind}`} aria-hidden="true">
                            <div className="lm-overlay__eyebrow">{moment.overlay.label}</div>
                            <div className="lm-overlay__body">{moment.overlay.value}</div>
                        </div>
                    )}
                    {showBadge && (
                        <div className="lm-badge" title="Licensed / original video asset not yet delivered">VIDEO_ASSET_REQUIRED</div>
                    )}
                </div>
            </div>
        </section>
    );
}

const LM_CSS = `
.lm-section { padding: 64px 24px; background: #FBF7EE; color: #0F1E3A; }
.lm-grid { max-width: 1160px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1.2fr; gap: 40px; align-items: center; }
.lm-grid--flip { grid-template-columns: 1.2fr 1fr; }
.lm-grid--flip .lm-copy { order: 2; }
.lm-grid--flip .lm-media { order: 1; }
@media (max-width: 900px) { .lm-grid, .lm-grid--flip { grid-template-columns: 1fr; gap: 24px; } .lm-grid--flip .lm-copy, .lm-grid--flip .lm-media { order: initial; } }
.lm-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.lm-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(28px, 4vw, 44px); font-weight: 500; letter-spacing: -0.02em; line-height: 1.05; margin: 0 0 14px; color: #0F1E3A; }
.lm-sub { font-size: 15px; line-height: 1.6; color: #57534E; max-width: 480px; margin: 0 0 22px; }
.lm-ctas { display: flex; flex-wrap: wrap; gap: 10px; }
.lm-cta { display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px; border-radius: 999px; font-size: 13px; font-weight: 600; text-decoration: none; }
.lm-cta--primary { background: #0F1E3A; color: #F6F3EC; }
.lm-cta--primary:hover { background: #C6A86B; color: #0F1E3A; }
.lm-cta--ghost { border: 1px solid rgba(15,30,58,0.24); color: #0F1E3A; }
.lm-cta--ghost:hover { border-color: #0F1E3A; }
.lm-media { position: relative; border-radius: 20px; overflow: hidden; aspect-ratio: 4 / 3; background: #0F1E3A; box-shadow: 0 30px 60px -30px rgba(15,30,58,0.35); }
.lm-poster, .lm-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.lm-video { z-index: 1; }
.lm-pause { position: absolute; bottom: 12px; right: 12px; z-index: 3; background: rgba(15,30,58,0.7); color: #F6F3EC; border: none; width: 30px; height: 30px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.lm-pause:hover { background: rgba(15,30,58,0.9); }
.lm-overlay { position: absolute; z-index: 2; bottom: 20px; left: 20px; background: rgba(255,255,255,0.94); backdrop-filter: blur(6px); border: 1px solid rgba(15,30,58,0.12); border-radius: 14px; padding: 10px 14px; box-shadow: 0 12px 24px -12px rgba(15,30,58,0.35); max-width: 60%; }
.lm-overlay__eyebrow { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 4px; }
.lm-overlay__body { font-size: 13px; font-weight: 600; color: #0F1E3A; }
.lm-badge { position: absolute; top: 12px; right: 12px; z-index: 3; background: rgba(198,168,107,0.95); color: #0F1E3A; font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; padding: 4px 8px; border-radius: 999px; }
`;
